import React from "react";
import { Box, Button, Divider, IconButton, ListItemIcon, ListItemText, MenuItem, MenuList, Popover, TextField, Typography } from "@mui/material";
import { CalendarMonth, Check, Close, KeyboardArrowDown } from "@mui/icons-material";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";
import AppDateField from "../../../lib/components/AppDateField";

/** Backend datePreset enum values. */
export const DATE_PRESETS = {
  TODAY: "TODAY",
  LAST_7_DAYS: "LAST_7_DAYS",
  LAST_30_DAYS: "LAST_30_DAYS",
  LAST_90_DAYS: "LAST_90_DAYS",
  THIS_MONTH: "THIS_MONTH",
  THIS_YEAR: "THIS_YEAR",
  ALL_TIME: "ALL_TIME",
  CUSTOM: "CUSTOM",
};

export const DATE_PRESET_OPTIONS = [
  { label: "Today", value: DATE_PRESETS.TODAY },
  { label: "Last 7 days", value: DATE_PRESETS.LAST_7_DAYS },
  { label: "Last 30 days", value: DATE_PRESETS.LAST_30_DAYS },
  { label: "Last 90 days", value: DATE_PRESETS.LAST_90_DAYS },
  { label: "This month", value: DATE_PRESETS.THIS_MONTH },
  { label: "This year", value: DATE_PRESETS.THIS_YEAR },
  { label: "All time", value: DATE_PRESETS.ALL_TIME },
  { label: "Custom", value: DATE_PRESETS.CUSTOM },
];

const PRESET_VALUES = new Set(Object.values(DATE_PRESETS));

/**
 * Normalize response/URL datePreset to a known backend enum value.
 * Defaults to THIS_MONTH (backend default when omitted).
 */
export function normalizeDatePreset(value) {
  if (!value) return DATE_PRESETS.THIS_MONTH;
  const key = String(value).trim().toUpperCase().replace(/\s+/g, "_");
  return PRESET_VALUES.has(key) ? key : DATE_PRESETS.THIS_MONTH;
}

/**
 * Pull date fields from response.filters regardless of naming variants.
 */
export function extractFilterDates(filters = {}) {
  if (!filters || typeof filters !== "object") {
    return { dateFrom: null, dateTo: null, datePreset: null };
  }

  return {
    datePreset: filters.datePreset || filters.preset || null,
    dateFrom: filters.dateFrom || filters.from || filters.startDate || filters.start || null,
    dateTo: filters.dateTo || filters.to || filters.endDate || filters.end || null,
  };
}

/**
 * Build date query params for firs-connected.
 * CUSTOM is the only preset that also needs dateFrom/dateTo (YYYY-MM-DD).
 */
export function buildDateParams(preset, customFrom, customTo) {
  const normalized = normalizeDatePreset(preset);

  if (normalized === DATE_PRESETS.CUSTOM) {
    if (!customFrom || !customTo) return {};
    return {
      datePreset: DATE_PRESETS.CUSTOM,
      dateFrom: moment(customFrom).format("YYYY-MM-DD"),
      dateTo: moment(customTo).format("YYYY-MM-DD"),
    };
  }

  return { datePreset: normalized };
}

/**
 * Compute date bounds for a preset in local time.
 * `dateTo` is exclusive (start of the next period), matching backend semantics.
 */
export function computePresetRange(preset) {
  const normalized = normalizeDatePreset(preset);
  const startOfToday = moment().startOf("day");

  switch (normalized) {
    case DATE_PRESETS.TODAY:
      return {
        dateFrom: startOfToday.clone().toISOString(),
        dateTo: startOfToday.clone().add(1, "day").toISOString(),
      };
    case DATE_PRESETS.LAST_7_DAYS:
      return {
        dateFrom: startOfToday.clone().subtract(6, "days").toISOString(),
        dateTo: startOfToday.clone().add(1, "day").toISOString(),
      };
    case DATE_PRESETS.LAST_30_DAYS:
      return {
        dateFrom: startOfToday.clone().subtract(29, "days").toISOString(),
        dateTo: startOfToday.clone().add(1, "day").toISOString(),
      };
    case DATE_PRESETS.LAST_90_DAYS:
      return {
        dateFrom: startOfToday.clone().subtract(89, "days").toISOString(),
        dateTo: startOfToday.clone().add(1, "day").toISOString(),
      };
    case DATE_PRESETS.THIS_MONTH:
      return {
        dateFrom: startOfToday.clone().startOf("month").toISOString(),
        dateTo: startOfToday.clone().startOf("month").add(1, "month").toISOString(),
      };
    case DATE_PRESETS.THIS_YEAR:
      return {
        dateFrom: startOfToday.clone().startOf("year").toISOString(),
        dateTo: startOfToday.clone().startOf("year").add(1, "year").toISOString(),
      };
    case DATE_PRESETS.ALL_TIME:
    case DATE_PRESETS.CUSTOM:
    default:
      return { dateFrom: null, dateTo: null };
  }
}

/**
 * Formats API timestamps for display in the user's local timezone.
 * Backend `dateTo` is exclusive (start of the next period).
 */
export function formatFilterDate(value, { exclusiveEnd = false } = {}) {
  if (!value) return null;
  const date = moment(value);
  if (!date.isValid()) return null;
  if (exclusiveEnd) date.subtract(1, "day");
  return date.format("MMM D, YYYY");
}

const DateRangePreset = ({
  datePreset = DATE_PRESETS.THIS_MONTH,
  dateFrom = null,
  dateTo = null,
  onPresetChange,
  onCustomRangeChange,
  onClear,
  presetOptions = DATE_PRESET_OPTIONS,
  customSelector = "range",
  /** When true, show only the preset name to keep the trigger button narrow. */
  compact = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const normalizedPreset = normalizeDatePreset(datePreset);
  const [isCustomMode, setIsCustomMode] = React.useState(normalizedPreset === DATE_PRESETS.CUSTOM);
  const [draftFrom, setDraftFrom] = React.useState(null);
  const [draftTo, setDraftTo] = React.useState(null);
  const [draftYear, setDraftYear] = React.useState(new Date().getFullYear());

  // Bounds for the year picker: the current year down to 30 years back.
  const yearBounds = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    return {
      maxDate: new Date(currentYear, 11, 31),
      minDate: new Date(currentYear - 29, 0, 1),
    };
  }, []);

  // Prefer API/URL dates; otherwise derive the range locally for the active preset.
  const resolvedRange = React.useMemo(() => {
    if (dateFrom || dateTo) {
      return { dateFrom, dateTo };
    }
    if (normalizedPreset === DATE_PRESETS.CUSTOM || normalizedPreset === DATE_PRESETS.ALL_TIME) {
      return { dateFrom: null, dateTo: null };
    }
    return computePresetRange(normalizedPreset);
  }, [dateFrom, dateTo, normalizedPreset]);

  React.useEffect(() => {
    setIsCustomMode(normalizedPreset === DATE_PRESETS.CUSTOM);
  }, [normalizedPreset]);

  React.useEffect(() => {
    const from = resolvedRange.dateFrom;
    const to = resolvedRange.dateTo;
    setDraftFrom(from ? moment(from).toDate() : null);
    // Backend dateTo is exclusive — convert to an inclusive end date for the picker.
    // The year selector already sends an inclusive end (YYYY-12-31), so keep as-is.
    setDraftTo(to ? (customSelector === "year" ? moment(to).toDate() : moment(to).subtract(1, "day").toDate()) : null);
    if (from) {
      const year = moment(from).year();
      if (year) setDraftYear(year);
    }
  }, [resolvedRange.dateFrom, resolvedRange.dateTo, customSelector]);

  const displayPreset = isCustomMode ? DATE_PRESETS.CUSTOM : normalizedPreset;
  const activeOption = presetOptions.find((item) => item.value === displayPreset) || presetOptions[0];
  const rangeLabel =
    resolvedRange.dateFrom || resolvedRange.dateTo
      ? [
          formatFilterDate(resolvedRange.dateFrom),
          // Date-range mode sends an exclusive end; the year selector sends an inclusive YYYY-12-31.
          formatFilterDate(resolvedRange.dateTo, { exclusiveEnd: customSelector !== "year" }),
        ]
          .filter(Boolean)
          .join(" – ")
      : null;

  // Active filter means a non-default date range is applied (preset selected, or custom dates set).
  const hasActiveFilter =
    Boolean(dateFrom || dateTo) || (!!datePreset && ![DATE_PRESETS.ALL_TIME, DATE_PRESETS.CUSTOM].includes(normalizedPreset));

  const handlePresetSelect = (value) => {
    if (value === DATE_PRESETS.CUSTOM) {
      setIsCustomMode(true);
      return;
    }
    setIsCustomMode(false);
    onPresetChange?.(value);
    setAnchorEl(null);
  };

  const handleApplyCustom = () => {
    if (!draftFrom || !draftTo) return;
    // API treats dateTo as exclusive — send the day after the user's inclusive end date.
    onCustomRangeChange?.({
      dateFrom: moment(draftFrom).format("YYYY-MM-DD"),
      dateTo: moment(draftTo).add(1, "day").format("YYYY-MM-DD"),
    });
    setIsCustomMode(true);
    setAnchorEl(null);
  };

  const handleApplyYear = () => {
    if (!draftYear) return;
    onCustomRangeChange?.({
      dateFrom: `${draftYear}-01-01`,
      dateTo: `${draftYear}-12-31`,
    });
    setIsCustomMode(true);
    setAnchorEl(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setIsCustomMode(normalizedPreset === DATE_PRESETS.CUSTOM);
  };

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Button
        variant="outlined"
        color="secondary"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        startIcon={<CalendarMonth sx={{ fontSize: compact ? 18 : 20 }} />}
        endIcon={<KeyboardArrowDown sx={{ fontSize: compact ? 18 : 20 }} />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          borderRadius: "10px",
          px: compact ? 1.25 : 2,
          minWidth: 0,
          height: 40,
          whiteSpace: "nowrap",
          backgroundColor: "#fff",
          borderColor: "#D1D5DB",
          color: "#374151",
          "& .MuiButton-startIcon": { mr: compact ? 0.5 : 1 },
          "& .MuiButton-endIcon": { ml: compact ? 0.25 : 1 },
        }}
      >
        {activeOption.label}
        {!compact && rangeLabel ? (
          <Typography component="span" variant="caption" sx={{ ml: 1, color: "#6B7280", fontWeight: 500 }}>
            {rangeLabel}
          </Typography>
        ) : null}
      </Button>

      {onClear && hasActiveFilter && (
        <IconButton
          size="small"
          onClick={onClear}
          title="Clear date filter"
          sx={{ color: "#6B7280", border: "1px solid #D1D5DB", borderRadius: "10px" }}
        >
          <Close fontSize="small" />
        </IconButton>
      )}

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { minWidth: 280, mt: 1, borderRadius: "12px", overflow: "hidden" } }}
      >
        <MenuList dense disablePadding>
          {presetOptions.map((option) => (
            <MenuItem
              key={option.value}
              selected={displayPreset === option.value}
              onClick={() => handlePresetSelect(option.value)}
            >
              <ListItemText primary={option.label} />
              {displayPreset === option.value ? (
                <ListItemIcon sx={{ minWidth: "auto", ml: 2 }}>
                  <Check fontSize="small" color="primary" />
                </ListItemIcon>
              ) : null}
            </MenuItem>
          ))}
        </MenuList>

        {isCustomMode &&
          (customSelector === "year" ? (
            <Box px={2} py={1.5}>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                      views={["year"]}
                      openTo="year"
                      label="Select year"
                      inputFormat="yyyy"
                      minDate={yearBounds.minDate}
                      maxDate={yearBounds.maxDate}
                      value={draftYear ? new Date(draftYear, 0, 1) : null}
                      onChange={(date) => {
                        if (date) setDraftYear(date.getFullYear());
                      }}
                      renderInput={(params) => <TextField fullWidth size="small" {...params} />}
                    />
                  </LocalizationProvider>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleApplyYear}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Apply year
                </Button>
              </Box>
            </Box>
          ) : (
            <Box px={2} py={1.5}>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexDirection="column" gap={1.5}>
                <AppDateField
                  label="Date From"
                  value={draftFrom}
                  maxDate={draftTo || undefined}
                  onChange={(value) => setDraftFrom(value)}
                />
                <AppDateField
                  label="Date To"
                  value={draftTo}
                  minDate={draftFrom || undefined}
                  onChange={(value) => setDraftTo(value)}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={!draftFrom || !draftTo}
                  onClick={handleApplyCustom}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  Apply range
                </Button>
              </Box>
            </Box>
          ))}
      </Popover>
    </Box>
  );
};

export default DateRangePreset;
